import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, Shield, Award, ThumbsUp, ChevronDown, ChevronUp, Star, ExternalLink, ClipboardCheck, Home, Wrench, ArrowRight, Snowflake, Sun, Wind } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16";

const COMMUNES = [
  { name: "Villeneuve-sur-Lot", url: "/installation-climatisation-villeneuve-sur-lot", distance: "—", pop: "~22 000 hab" },
  { name: "Tonneins", url: "/installation-climatisation-tonneins", distance: "25 min", pop: "~9 300 hab" },
  { name: "Aiguillon", url: "/installation-climatisation-aiguillon", distance: "30 min", pop: "~4 400 hab" },
  { name: "Fumel", url: "/installation-climatisation-fumel", distance: "20 min", pop: "~5 000 hab" },
  { name: "Sainte-Livrade-sur-Lot", url: "/installation-climatisation-sainte-livrade-sur-lot", distance: "10 min", pop: "~6 200 hab" },
];

const FAQS = [
  {
    question: "Combien coûte l'installation d'une climatisation en Lot-et-Garonne ?",
    answer: "Le prix d'une climatisation en Lot-et-Garonne varie selon le type d'appareil. Un mono-split coûte entre 1 500€ et 3 000€ posé, un multi-split entre 3 000€ et 6 000€, et une climatisation gainable entre 5 000€ et 10 000€. François Delattre se déplace gratuitement chez vous pour un devis personnalisé."
  },
  {
    question: "Quelle climatisation choisir : mono-split, multi-split ou gainable ?",
    answer: "Le mono-split climatise une seule pièce — idéal pour un salon ou une chambre. Le multi-split permet de climatiser plusieurs pièces avec une seule unité extérieure. Le gainable est invisible : l'air est diffusé par des grilles au plafond dans toute la maison. François vous conseille la solution la plus adaptée à votre logement après visite gratuite."
  },
  {
    question: "François Delattre installe-t-il la climatisation dans tout le 47 ?",
    answer: "Oui, François intervient dans tout le Lot-et-Garonne depuis sa base de Pinel-Hauterive. Il se déplace régulièrement à Villeneuve-sur-Lot, Tonneins, Aiguillon, Fumel, Sainte-Livrade-sur-Lot et dans l'ensemble du département. Déplacement et devis gratuits."
  },
  {
    question: "Peut-on installer une climatisation réversible pour chauffer aussi ?",
    answer: "Absolument. La climatisation réversible (PAC air/air) produit du froid en été et du chaud en hiver. C'est une solution deux-en-un très populaire en Lot-et-Garonne où les étés sont chauds et les hivers modérés. Elle peut être éligible aux aides MaPrimeRénov' et CEE selon les conditions."
  },
  {
    question: "Combien de temps pour installer une climatisation ?",
    answer: "Un mono-split se pose en une demi-journée. Un multi-split ou un gainable prend 1 à 2 jours selon la complexité. François planifie votre chantier pour minimiser la gêne et assure une mise en service le jour même."
  },
  {
    question: "La climatisation est-elle éligible aux aides de l'État ?",
    answer: "La climatisation seule (rafraîchissement uniquement) n'est pas éligible aux aides. En revanche, la PAC air/air réversible — qui est une climatisation réversible — peut bénéficier des primes CEE. François vous informe sur les aides disponibles selon votre situation."
  }
];

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function ReviewCard({ review }) {
  return (
    <Card className="bg-white border border-gray-100 rounded-xl shadow-sm h-full">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          {review.photo ? (
            <img src={review.photo} alt={review.name} className="w-9 h-9 rounded-full object-cover" width={36} height={36} />
          ) : (
            <div className="w-9 h-9 bg-[#6AB023] rounded-full flex items-center justify-center text-white font-bold text-xs">{getInitials(review.name)}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
            <p className="text-xs text-gray-400">{review.date}</p>
          </div>
          <GoogleLogo />
        </div>
        <StarRating rating={review.rating} />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{review.text}</p>
      </CardContent>
    </Card>
  );
}

function FAQItem({ faq, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
        <span className="font-semibold text-gray-900 text-sm md:text-base">{faq.question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#6AB023] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#6AB023] flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 bg-white"><p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p></div>}
    </div>
  );
}

function MiniReviewBadge({ rating, total }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <GoogleLogo />
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
        ))}
      </div>
      <span className="font-semibold text-gray-900">{rating.toFixed(1)}</span>
      <span className="text-gray-400">({total} avis)</span>
    </div>
  );
}

function ContactForm({ rating, total }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("Climatisation mono-split");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) { setError("Veuillez remplir votre nom et téléphone."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), service, message: message.trim() });
      setSuccess(true);
      setName(""); setPhone(""); setService("Climatisation mono-split"); setMessage("");
    } catch { setError("Une erreur est survenue. Veuillez nous appeler directement au 06 28 51 49 15."); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="flex flex-col items-center text-center py-8">
      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4"><CheckCircle className="w-8 h-8 text-[#6AB023]" /></div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
      <p className="text-gray-600 text-sm">François vous rappelle sous 24h pour organiser sa venue chez vous.</p>
      <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6 border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full">Nouvelle demande</Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Devis climatisation gratuit</h3>
        <p className="text-sm text-gray-500">François vous rappelle sous 24h.</p>
      </div>

      {total > 0 && (
        <div className="review-badge-pulse bg-green-50/60 rounded-lg p-3 flex items-center justify-center">
          <MiniReviewBadge rating={rating} total={total} />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre nom <span className="text-red-400">*</span></label>
        <Input type="text" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre téléphone <span className="text-red-400">*</span></label>
        <Input type="tel" placeholder="06 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de climatisation</label>
        <select value={service} onChange={(e) => setService(e.target.value)} className="w-full h-12 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none">
          <option value="Climatisation mono-split">Climatisation mono-split</option>
          <option value="Climatisation multi-split">Climatisation multi-split</option>
          <option value="Climatisation gainable">Climatisation gainable</option>
          <option value="Climatisation réversible">Climatisation réversible (chaud + froid)</option>
          <option value="PAC Air/Air">PAC Air/Air réversible</option>
          <option value="Autre">Autre</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre commune <span className="text-xs text-gray-400 font-normal">(optionnel)</span></label>
        <Input type="text" placeholder="Ex: Villeneuve-sur-Lot, Tonneins..." value={message} onChange={(e) => setMessage(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 text-base gap-2">
        {loading ? <span className="flex items-center gap-2"><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Envoi en cours...</span> : <><Send className="w-5 h-5" />Recevoir mon devis gratuit</>}
      </Button>
      <p className="text-xs text-gray-400 text-center">Sans engagement · Vos données restent confidentielles</p>
    </form>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] p-3 md:hidden">
      <a href="#devis" className="block">
        <Button className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 gap-2 text-base">
          <Send className="w-5 h-5" />Devis climatisation gratuit
        </Button>
      </a>
    </div>
  );
}

export default function InstallationClimLotEtGaronne() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(res => {
      setReviews(res.data.reviews);
      setRating(res.data.rating);
      setTotal(res.data.total);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("revealed");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
      );
      const elements = document.querySelectorAll(".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale");
      elements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, [reviews]);

  useEffect(() => {
    document.title = "Installation Climatisation Lot-et-Garonne (47) | Alter Energies";
    const meta = document.querySelector('meta[name="description"]');
    const content = "Installation climatisation mono-split, multi-split et gainable en Lot-et-Garonne. François Delattre, artisan certifié RGE, intervient dans tout le 47. Devis gratuit.";
    if (meta) { meta.setAttribute("content", content); } else { const m = document.createElement("meta"); m.name = "description"; m.content = content; document.head.appendChild(m); }
  }, []);

  return (
    <div className="min-h-screen bg-white">

      <StickyMobileCTA />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="hero-badge hero-badge-shimmer inline-flex items-center gap-2 bg-blue-100 text-blue-600 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <Snowflake className="w-4 h-4" />Lot-et-Garonne (47) · Tout le département
              </div>
              <h1 className="hero-title text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                Installation Climatisation en <span className="text-[#6AB023]">Lot-et-Garonne</span>
              </h1>
              <p className="hero-subtitle text-lg text-gray-600 mb-8 leading-relaxed">
                Les étés sont chauds dans le 47. François Delattre, artisan certifié RGE avec 20 ans d'expérience, installe votre climatisation mono-split, multi-split ou gainable partout en Lot-et-Garonne. Devis gratuit, intervention rapide.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 mb-8">
                <a href="tel:0628514915"><Button className="bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 gap-2 text-base"><Phone className="w-5 h-5" />06 28 51 49 15</Button></a>
                <a href="#devis"><Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 font-bold rounded-full px-8 h-12 text-base">Devis gratuit →</Button></a>
              </div>
              <div className="hero-trust flex flex-wrap gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#6AB023]" />Certifié RGE</span>
                <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#6AB023]" />500+ installations</span>
                <span className="flex items-center gap-1.5"><Snowflake className="w-4 h-4 text-[#6AB023]" />Mono-split, multi-split, gainable</span>
              </div>
            </div>
            <div className="hero-image relative">
              <div className="bg-blue-50 rounded-2xl p-2">
                <img src="https://customer-assets.emergentagent.com/job_chauffage-pac/artifacts/d8avbt7b_IMG_8167.jpeg" alt="Installation climatisation en Lot-et-Garonne - Alter Energies artisan RGE 47" className="w-full rounded-xl object-cover" width={600} height={400} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><Snowflake className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">20 ans d'expérience</p>
                  <p className="text-xs text-gray-500">dans tout le 47</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="py-12 bg-[#6AB023]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "500+", label: "Installations réalisées" },
              { value: "20 ans", label: "D'expérience" },
              { value: "½ jour", label: "Pose mono-split" },
              { value: "Gratuit", label: "Devis et déplacement" },
            ].map((stat, idx) => (
              <div key={stat.label} className={`scroll-reveal-scale stagger-${idx + 1}`}>
                <p className="text-3xl md:text-4xl font-extrabold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types de clim */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Trois <span className="text-[#6AB023]">solutions clim</span> pour votre confort</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">François vous conseille la climatisation la mieux adaptée à votre logement, votre budget et vos besoins.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Wind, title: "Mono-split", price: "1 500€ - 3 000€", desc: "Un groupe extérieur, une unité intérieure. La solution la plus simple et économique pour climatiser une pièce de vie — salon, chambre ou bureau.", points: ["Climatise une pièce efficacement", "Installation en une demi-journée", "Solution la plus abordable", "Idéal pour un premier équipement"] },
              { icon: Sun, title: "Multi-split", price: "3 000€ - 6 000€", desc: "Un groupe extérieur, plusieurs unités intérieures. Climatisez 2 à 5 pièces avec un seul appareil extérieur pour un confort complet.", points: ["2 à 5 pièces climatisées", "Un seul groupe extérieur", "Pilotage indépendant par pièce", "Réversible chaud/froid disponible"] },
              { icon: Snowflake, title: "Gainable", price: "5 000€ - 10 000€", desc: "L'installation invisible. L'air est diffusé par des grilles discrètes au plafond dans toute la maison. Le haut de gamme du confort.", points: ["100% invisible — aucune unité apparente", "Climatisation homogène dans toute la maison", "Discrétion totale, zéro bruit", "Valorise votre bien immobilier"] },
            ].map((clim, idx) => (
              <Card key={clim.title} className={`scroll-reveal-scale stagger-${idx + 1} border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow`}>
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4"><clim.icon className="w-6 h-6 text-blue-600" /></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{clim.title}</h3>
                  <p className="text-[#6AB023] font-bold text-sm mb-3">{clim.price}</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{clim.desc}</p>
                  <ul className="space-y-2">
                    {clim.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-700"><span className="text-[#6AB023] font-bold mt-0.5">→</span>{point}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10 scroll-reveal">
            <a href="#devis"><Button className="btn-shine bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 text-base gap-2"><Send className="w-5 h-5" />Demander un devis gratuit</Button></a>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Climatisation dans <span className="text-[#6AB023]">tout le Lot-et-Garonne</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">François se déplace gratuitement chez vous. Trouvez votre commune ci-dessous.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {COMMUNES.map((commune, idx) => (
      <a
                key={commune.name}
                href={commune.url}
                className={`scroll-reveal-scale stagger-${(idx % 3) + 1} group flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-[#6AB023] hover:shadow-md transition-all`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors">
                    <MapPin className="w-4 h-4 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm group-hover:text-[#6AB023] transition-colors">{commune.name}</p>
                    <p className="text-xs text-gray-400">{commune.distance} · {commune.pop}</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#6AB023] transition-colors" />
              </a>
            ))}
          </div>
          <div className="text-center mt-8 scroll-reveal">
            <p className="text-sm text-gray-500 mb-4">Votre commune n'est pas listée ? François se déplace partout dans le 47.</p>
            <a href="#devis"><Button className="btn-shine bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 text-base gap-2"><Send className="w-5 h-5" />Demander un devis gratuit</Button></a>
          </div>
        </div>
      </section>

      {/* Parcours client */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre clim installée <span className="text-[#6AB023]">rapidement</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Un processus simple, un seul interlocuteur. François gère tout de A à Z.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: "1", icon: Phone, title: "Contactez-nous", desc: "Un appel ou un formulaire. François vous rappelle sous 24h pour discuter de vos besoins en climatisation." },
              { step: "2", icon: ClipboardCheck, title: "Visite gratuite à domicile", desc: "François vient chez vous pour évaluer les pièces à climatiser, les contraintes techniques et vous remettre un devis détaillé." },
              { step: "3", icon: Wrench, title: "Pose rapide", desc: "Installation soignée en une demi-journée (mono-split) à 2 jours (gainable). Mise en service et explication du fonctionnement le jour même." },
            ].map((item, idx) => (
              <div key={item.step} className={`relative text-center scroll-reveal-scale stagger-${idx + 1}`}>
                <div className="w-14 h-14 bg-[#6AB023] rounded-full flex items-center justify-center mx-auto mb-5 text-white font-extrabold text-xl shadow-md">{item.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pourquoi <span className="text-[#6AB023]">Alter Energies</span> pour votre clim ?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Certifié RGE", desc: "Un gage de qualité et de sérieux pour votre installation." },
              { icon: Award, title: "500+ installations", desc: "20 ans d'expérience en climatisation et pompe à chaleur." },
              { icon: MapPin, title: "Tout le 47", desc: "Déplacement et devis gratuits dans tout le département." },
              { icon: ThumbsUp, title: "5 étoiles Google", desc: "La satisfaction de nos clients parle d'elle-même." },
            ].map((item, idx) => (
              <Card key={item.title} className={`scroll-reveal-scale stagger-${idx + 1} service-card border border-gray-100 rounded-xl shadow-sm text-center`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4"><item.icon className="w-6 h-6 text-blue-600" /></div>
                  <p className="font-bold text-gray-900 mb-2">{item.title}</p>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Avis Google */}
      {reviews.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center mb-10 scroll-reveal">
              <div className="inline-flex items-center gap-2 mb-4"><GoogleLogo /><span className="text-sm font-medium text-gray-500">Avis Google vérifiés</span></div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">La référence clim en <span className="text-[#6AB023]">Lot-et-Garonne</span></h2>
              <div className="flex items-center justify-center gap-3 mt-4">
                <span className="text-4xl font-bold text-gray-900">{rating.toFixed(1)}</span>
                <div><StarRating rating={Math.round(rating)} /><p className="text-xs text-gray-500 mt-1">{total} avis sur Google</p></div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {reviews.slice(0, 3).map((review, idx) => (
                <div key={review.name} className={`scroll-reveal-scale stagger-${idx + 1}`}><ReviewCard review={review} /></div>
              ))}
            </div>
            <div className="text-center scroll-reveal">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full px-8 h-11 font-semibold gap-2"><GoogleLogo />Voir nos {total > 0 ? total : ""} avis Google<ExternalLink className="w-4 h-4" /></Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Maillage interne PAC */}
      <section className="py-12 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="scroll-reveal text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Aussi spécialiste en <span className="text-[#6AB023]">pompe à chaleur</span></h2>
            <p className="text-sm text-gray-600">François installe aussi des pompes à chaleur air/eau et air/air dans tout le Lot-et-Garonne.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 scroll-reveal">
            <a href="/installation-pompe-a-chaleur-lot-et-garonne" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#6AB023]/30 rounded-full text-sm font-medium text-[#6AB023] hover:bg-green-50 transition-colors">Installation PAC Lot-et-Garonne</a>
            <a href="/installation-pompe-a-chaleur-villeneuve-sur-lot" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">PAC Villeneuve-sur-Lot</a>
            <a href="/installation-pompe-a-chaleur-tonneins" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">PAC Tonneins</a>
            <a href="/installation-pompe-a-chaleur-aiguillon" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">PAC Aiguillon</a>
          </div>
        </div>
      </section>

      {/* Formulaire devis */}
      <section id="devis" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre <span className="text-[#6AB023]">devis climatisation</span> gratuit</h2>
            <p className="text-gray-600">Installation climatisation en Lot-et-Garonne — Gratuit et sans engagement</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <Card className="scroll-reveal-left bg-white border border-gray-200 rounded-xl shadow-lg">
              <CardContent className="p-6 md:p-8"><ContactForm rating={rating} total={total} /></CardContent>
            </Card>
            <div className="flex flex-col gap-5 justify-center scroll-reveal-right">
              <a href="tel:0628514915" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Phone className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">06 28 51 49 15</p><p className="text-xs text-gray-500">Appel gratuit · Du lundi au samedi</p></div>
              </a>
              <a href="mailto:frs.delattre@gmail.com" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Mail className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">frs.delattre@gmail.com</p><p className="text-xs text-gray-500">Réponse sous 24h</p></div>
              </a>
              <div className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-11 h-11 bg-blue-50 rounded-full flex items-center justify-center"><MapPin className="w-5 h-5 text-blue-600" /></div>
                <div><p className="font-bold text-gray-900">Basé à Pinel-Hauterive</p><p className="text-xs text-gray-500">Déplacement gratuit dans tout le 47</p></div>
              </div>

              <div className="bg-blue-50/60 rounded-xl p-5 space-y-3 mt-2">
                <p className="text-sm font-semibold text-gray-900">Ce que François fait pour vous :</p>
                <div className="space-y-2">
                  {[
                    "Déplacement et diagnostic gratuits dans tout le 47",
                    "Devis détaillé avec choix du type de clim adapté",
                    "Installation soignée et mise en service le jour même",
                    "SAV et conseil toute l'année",
                  ].map((item) => (
                    <p key={item} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#6AB023] flex-shrink-0 mt-0.5" />{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions fréquentes — <span className="text-[#6AB023]">Climatisation en Lot-et-Garonne</span></h2>
          </div>
          <div className="space-y-3 scroll-reveal">
            {FAQS.map((faq, index) => <FAQItem key={faq.question} faq={faq} defaultOpen={index === 0} />)}
          </div>
        </div>
      </section>

      <div className="h-16 md:hidden" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Installation climatisation Lot-et-Garonne",
        "description": "Installation de climatisation mono-split, multi-split et gainable dans tout le Lot-et-Garonne par Alter Energies, artisan certifié RGE. Devis gratuit, intervention rapide.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Alter Energies - François Delattre",
          "telephone": "+33628514915",
          "email": "frs.delattre@gmail.com",
          "address": { "@type": "PostalAddress", "streetAddress": "1899 Côte de Pinel Garrigou", "addressLocality": "Pinel-Hauterive", "postalCode": "47380", "addressRegion": "Lot-et-Garonne", "addressCountry": "FR" },
          "geo": { "@type": "GeoCoordinates", "latitude": "44.3833", "longitude": "0.7667" },
          "url": "https://alterenergies-47.com",
          "priceRange": "€€",
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "21", "bestRating": "5" }
        },
        "areaServed": [
          { "@type": "AdministrativeArea", "name": "Lot-et-Garonne" },
          { "@type": "City", "name": "Villeneuve-sur-Lot" },
          { "@type": "City", "name": "Tonneins" },
          { "@type": "City", "name": "Aiguillon" },
          { "@type": "City", "name": "Fumel" },
          { "@type": "City", "name": "Sainte-Livrade-sur-Lot" }
        ],
        "serviceType": ["Installation climatisation", "Climatisation mono-split", "Climatisation multi-split", "Climatisation gainable", "Climatisation réversible"]
      })}} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      })}} />

    </div>
  );
}
